
import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { AnalysisCatalogItem, AnalysisResultOption } from '../../types';
import { Plus, Trash2, X, Loader2, Database, List, Type, Hash, Eye, EyeOff, Layers, Search, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import LoincSearchModal from './LoincSearchModal';

const AdminCatalog: React.FC = () => {
  const [catalogItems, setCatalogItems] = useState<AnalysisCatalogItem[]>([]);
  const [newAnalysisName, setNewAnalysisName] = useState('');
  const [newAnalysisDesc, setNewAnalysisDesc] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const [loincCode, setLoincCode] = useState('');
  const [loincComponent, setLoincComponent] = useState('');
  const [loincSystem, setLoincSystem] = useState('');
  const [unit, setUnit] = useState(''); 
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPanel, setIsPanel] = useState(false);
  const [selectedSubAnalyses, setSelectedSubAnalyses] = useState<string[]>([]);
  const [resultType, setResultType] = useState<'text' | 'choice' | 'numeric'>('text');
  const [tempResults, setTempResults] = useState<AnalysisResultOption[]>([]);
  const [currentResultInput, setCurrentResultInput] = useState('');
  const [currentResultSnomed, setCurrentResultSnomed] = useState('');
  const [minRef, setMinRef] = useState('');
  const [maxRef, setMaxRef] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoincModalOpen, setIsLoincModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    fetchCatalog();
  }, []);

  const fetchCatalog = async () => {
    if (!isSupabaseConfigured()) {
        setCatalogItems([
            { id: '1', name: 'Microscopie', description: 'Examen direct Ziehl-Neelsen', possible_results: ['Négatif', 'Positif 1+', 'Positif 2+', 'Positif 3+'], result_type: 'choice', created_at: '', loinc_code: '11545-1', unit: '', active: true },
            { id: '2', name: 'GeneXpert', description: 'PCR temps réel', possible_results: ['MTB Non Détecté', 'MTB Détecté, Rif R', 'MTB Détecté, Rif S'], result_type: 'choice', created_at: '', loinc_code: '91465-5', unit: '', active: true },
            { id: '3', name: 'Leucocytes', description: 'Numération', possible_results: [], result_type: 'numeric', min_ref: 4000, max_ref: 10000, created_at: '', loinc_code: '6690-2', unit: '/mm3', active: true },
            { id: '4', name: 'Antibiogramme HN', description: 'Test de sensibilité', possible_results: ['H', 'N', 'I'], result_type: 'choice', created_at: '', active: false }
        ]);
        return;
    }
    const { data } = await supabase.from('analysis_catalog').select('*').order('name');
    if (data) setCatalogItems(data as AnalysisCatalogItem[]);
  };

  const addResultOption = () => {
      if (currentResultInput.trim()) {
          setTempResults([...tempResults, { value: currentResultInput.trim(), snomed_code: currentResultSnomed.trim() }]);
          setCurrentResultInput('');
          setCurrentResultSnomed('');
      }
  };

  const removeResultOption = (index: number) => {
      const newResults = [...tempResults];
      newResults.splice(index, 1);
      setTempResults(newResults);
  };

  const handleToggleSubAnalysis = (id: string) => {
      setSelectedSubAnalyses(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleAddAnalysis = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAnalysisName.trim()) return;
    setIsSubmitting(true);
    
    let finalPossibleResults: string[] | null = null;
    let finalResultOptions: AnalysisResultOption[] | null = null;
    let finalSubAnalyses: string[] | null = null;

    if (isPanel) {
        finalSubAnalyses = selectedSubAnalyses;
    } else {
        if (resultType === 'choice') {
            const optionsToSave = [...tempResults];
            if (currentResultInput.trim()) {
                optionsToSave.push({ value: currentResultInput.trim(), snomed_code: currentResultSnomed.trim() });
            }
            if (optionsToSave.length > 0) {
                finalResultOptions = optionsToSave;
                finalPossibleResults = optionsToSave.map(o => o.value);
            }
        }
    }

    const payload: any = {
        name: newAnalysisName,
        description: newAnalysisDesc,
        loinc_code: loincCode,
        loinc_component: loincComponent,
        loinc_system: loincSystem,
        result_type: isPanel ? null : resultType,
        possible_results: finalPossibleResults,
        result_options: finalResultOptions,
        sub_analyses: finalSubAnalyses,
        active: true
    };

    if (!isPanel && resultType === 'numeric') {
        payload.unit = unit;
        if (minRef) payload.min_ref = parseFloat(minRef);
        if (maxRef) payload.max_ref = parseFloat(maxRef);
    }

    if (isSupabaseConfigured()) {
        const { error } = await supabase.from('analysis_catalog').insert([payload]);
        if (error) alert("Erreur: " + error.message);
        else fetchCatalog();
    } else {
        setCatalogItems([...catalogItems, { id: Math.random().toString(), ...payload, created_at: '' }]);
    }
    resetForm();
  };

  const resetForm = () => {
    setNewAnalysisName(''); setNewAnalysisDesc(''); setLoincCode(''); setLoincComponent(''); setLoincSystem('');
    setUnit(''); setResultType('text'); setTempResults([]); setCurrentResultInput(''); setCurrentResultSnomed('');
    setMinRef(''); setMaxRef(''); setIsPanel(false); setSelectedSubAnalyses([]); setIsSubmitting(false); setIsModalOpen(false);
  };

  const handleDeleteAnalysis = async (id: string) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce type d'analyse ?")) return;
    if (isSupabaseConfigured()) {
        const { error } = await supabase.from('analysis_catalog').delete().eq('id', id);
        if (error) alert("Erreur: " + error.message);
        else fetchCatalog();
    } else {
        setCatalogItems(prev => prev.filter(i => i.id !== id));
    }
  };

  const toggleAnalysisStatus = async (item: AnalysisCatalogItem) => {
      const newStatus = !item.active;
      if (isSupabaseConfigured()) {
          const { error } = await supabase.from('analysis_catalog').update({ active: newStatus }).eq('id', item.id);
          if (error) alert("Erreur: " + error.message);
          else fetchCatalog();
      } else {
          setCatalogItems(prev => prev.map(i => i.id === item.id ? { ...i, active: newStatus } : i));
      }
  };
  
  const handleLoincSelect = (loinc: any) => {
    setLoincCode(loinc.LOINC_NUM || '');
    setNewAnalysisName(loinc.LONG_COMMON_NAME || '');
    setNewAnalysisDesc(loinc.DefinitionDescription || loinc.TermDescriptions?.[0]?.Description || '');
    setLoincComponent(loinc.COMPONENT || '');
    setLoincSystem(loinc.SYSTEM || '');
    setUnit(loinc.EXAMPLE_UCUM_UNITS || loinc.EXAMPLE_UNITS || '');
    setIsLoincModalOpen(false);
  };

  const filteredItems = catalogItems.filter(item => {
      const matchSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
      if (filterStatus === 'all') return matchSearch;
      if (filterStatus === 'active') return matchSearch && item.active !== false;
      return matchSearch && item.active === false;
  });

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const currentItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="flex flex-col h-full overflow-hidden">
        {/* BARRE DE FILTRES FIXE */}
        <div className="p-4 bg-white border-b border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
            <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input 
                        type="text" 
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        placeholder="Rechercher une analyse..." 
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                </div>
                <div className="flex items-center bg-slate-100 p-1 rounded-lg self-start shrink-0">
                    <button onClick={() => { setFilterStatus('all'); setCurrentPage(1); }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filterStatus === 'all' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>Tout</button>
                    <button onClick={() => { setFilterStatus('active'); setCurrentPage(1); }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filterStatus === 'active' ? 'bg-white shadow text-green-700' : 'text-slate-500 hover:text-slate-700'}`}>Actifs</button>
                    <button onClick={() => { setFilterStatus('inactive'); setCurrentPage(1); }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filterStatus === 'inactive' ? 'bg-white shadow text-slate-700' : 'text-slate-500 hover:text-slate-700'}`}>Inactifs</button>
                </div>
            </div>

            <div className="flex items-center gap-4">
                 {filteredItems.length > 0 && (
                    <div className="flex items-center gap-2">
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-50 text-slate-600 border border-transparent hover:border-slate-200 transition-colors">
                            <ChevronLeft size={20} />
                        </button>
                        <span className="text-sm font-medium whitespace-nowrap px-2">{currentPage} / {totalPages}</span>
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-50 text-slate-600 border border-transparent hover:border-slate-200 transition-colors">
                            <ChevronRight size={20} />
                        </button>
                    </div>
                 )}
                <button onClick={() => setIsModalOpen(true)} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2 shadow-sm shrink-0">
                    <Plus size={16}/> Nouvelle Analyse
                </button>
            </div>
        </div>

        {/* TABLEAU SCROLLABLE */}
        <div className="flex-1 overflow-auto bg-white">
            <table className="w-full text-left text-sm text-slate-600 border-collapse">
                <thead className="sticky top-0 z-10 bg-slate-100 text-slate-900 font-bold border-b-2 border-slate-200 shadow-sm">
                    <tr>
                        <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Nom de l'Analyse</th>
                        <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Code LIMS (LOINC)</th>
                        <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Type</th>
                        <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Configuration</th>
                        <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Statut</th>
                        <th className="px-6 py-4 uppercase tracking-wider text-[11px] text-right">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {currentItems.map(item => (
                        <tr key={item.id} className={`hover:bg-slate-50 transition-colors ${item.active === false ? 'opacity-60 bg-slate-50' : ''}`}>
                            <td className="px-6 py-4">
                                <div className="font-bold text-slate-800">{item.name}</div>
                                {item.description && <div className="text-xs text-slate-500 mt-0.5 max-w-xs truncate">{item.description}</div>}
                            </td>
                            <td className="px-6 py-4">
                                {item.loinc_code ? <span className="font-mono text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-100">{item.loinc_code}</span> : <span className="text-slate-300 italic">-</span>}
                            </td>
                            <td className="px-6 py-4">
                                {item.sub_analyses && item.sub_analyses.length > 0 ? (
                                    <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-1 rounded-full uppercase font-bold tracking-wider flex w-fit items-center gap-1"><Layers size={10} /> Panel</span>
                                ) : (
                                    <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-1 rounded-full uppercase font-bold tracking-wider flex w-fit items-center gap-1">
                                        {item.result_type === 'numeric' ? <Hash size={10} /> : item.result_type === 'choice' ? <List size={10} /> : <Type size={10} />}
                                        {item.result_type || 'texte'}
                                    </span>
                                )}
                            </td>
                            <td className="px-6 py-4">
                                {item.result_type === 'numeric' ? <div className="text-xs font-mono text-slate-600">[{item.min_ref ?? '?'}-{item.max_ref ?? '?'}] {item.unit}</div> : (item.possible_results?.length || 0) > 0 ? <div className="text-xs text-slate-500">{item.possible_results?.length} choix</div> : <span className="text-slate-400 italic text-xs">Libre</span>}
                            </td>
                            <td className="px-6 py-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${item.active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>{item.active !== false ? 'Actif' : 'Inactif'}</span>
                            </td>
                            <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                    <button onClick={() => toggleAnalysisStatus(item)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 rounded transition-colors">
                                        {item.active === false ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                    <button onClick={() => handleDeleteAnalysis(item.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"><Trash2 size={16} /></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {currentItems.length === 0 && (
                         <tr><td colSpan={6} className="p-12 text-center text-slate-400 italic">Aucune analyse trouvée</td></tr>
                    )}
                </tbody>
            </table>
        </div>

        {isModalOpen && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95">
                    <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                        <h3 className="font-bold text-lg text-slate-800">Nouvelle Analyse</h3>
                        <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
                    </div>
                    <div className="p-6 overflow-y-auto">
                        <form onSubmit={handleAddAnalysis} className="space-y-4">
                            <div className="flex bg-slate-100 p-1 rounded-lg w-full mb-4">
                                <button type="button" onClick={() => setIsPanel(false)} className={`flex-1 text-sm font-medium py-2 rounded-md transition-all ${!isPanel ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}>Simple</button>
                                <button type="button" onClick={() => setIsPanel(true)} className={`flex-1 text-sm font-medium py-2 rounded-md transition-all ${isPanel ? 'bg-white text-purple-600 shadow-sm' : 'text-slate-500'}`}>Panel</button>
                            </div>
                            <div className="space-y-4">
                                <div><label className="block text-xs font-bold text-slate-500 mb-1">Nom</label><input required value={newAnalysisName} onChange={(e) => setNewAnalysisName(e.target.value)} className="w-full p-2.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" /></div>
                                <div><label className="block text-xs font-bold text-slate-500 mb-1">LOINC</label><div className="flex gap-2"><input value={loincCode} onChange={(e) => setLoincCode(e.target.value)} className="flex-1 p-2 text-xs border border-slate-300 rounded font-mono" /><button type="button" onClick={() => setIsLoincModalOpen(true)} className="px-3 py-1 text-xs bg-slate-100 border rounded hover:bg-slate-200">Chercher</button></div></div>
                                {!isPanel && (
                                    <div className="grid grid-cols-3 gap-3">
                                        <button type="button" onClick={() => setResultType('text')} className={`p-2 border rounded text-xs font-bold uppercase ${resultType === 'text' ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white'}`}>Texte</button>
                                        <button type="button" onClick={() => setResultType('numeric')} className={`p-2 border rounded text-xs font-bold uppercase ${resultType === 'numeric' ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white'}`}>Nombre</button>
                                        <button type="button" onClick={() => setResultType('choice')} className={`p-2 border rounded text-xs font-bold uppercase ${resultType === 'choice' ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white'}`}>Choix</button>
                                    </div>
                                )}
                            </div>
                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium">Annuler</button>
                                <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 flex items-center gap-2">
                                    {isSubmitting && <Loader2 size={16} className="animate-spin" />} Enregistrer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        )}
        {isLoincModalOpen && <LoincSearchModal onClose={() => setIsLoincModalOpen(false)} onSelect={handleLoincSelect} />}
    </div>
  );
};

export default AdminCatalog;
